import { Injectable, Inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  checkValidResponse,
  logError,
  FilterOption,
  Message,
  MessageType,
  SortOrderType,
  MessageFilters,
  MessageUpdate,
  MessagesResponse,
  ServiceError,
} from '@cigna/chcp/shared/util';

@Injectable({
  providedIn: 'root',
})
export class MessageCenterService {
  private messageCenterApi: string;
  private presignedUrlApi: string;
  private CONSUMER_CODE = '1000';

  constructor(
    private http: HttpClient,
    @Inject('secureApi') secureApi: string,
    private datePipe: DatePipe,
  ) {
    this.messageCenterApi = `${secureApi}/mcenter/v1`;
    this.presignedUrlApi = `${secureApi}/claims/reports/v1/get-presigned-url`;
  }

  fetchMessages({
    messagesType,
    sortOrder,
    filters,
    offset,
    notfnType,
  }: {
    messagesType: MessageType;
    sortOrder?: SortOrderType;
    filters?: MessageFilters;
    offset?: string;
    notfnType?: string;
  }): Observable<MessagesResponse | ServiceError> {
    let params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );

    if (notfnType) {
      params = params.append('notfnType', notfnType);
    }
    if (offset) {
      params = params.append('offset', offset);
    }
    if (sortOrder === 'asc') {
      params = params.append('sort', sortOrder);
    }
    if (filters?.flag?.some((v) => v.isSelected)) {
      params = params.append('flagged', 'true');
    }
    if (filters?.read?.some((v) => v.isSelected)) {
      params = params.append('unread', 'true');
    }
    if (filters?.date?.min) {
      params = params.append(
        'startDate',
        filters.date.min.valueOf().toString(),
      );
    }
    if (filters?.date?.max) {
      params = params.append('endDate', filters.date.max.valueOf().toString());
    }
    if (filters?.providers?.some((v) => v.isSelected)) {
      params = params.append(
        'tins',
        filters.providers
          .filter((v) => v.isSelected)
          .map((v) => v.value)
          .join(','),
      );
    }
    if (filters?.type) {
      const typeFilterOptions: FilterOption[] = Object.values(
        filters.type,
      ).reduce((acc, val) => acc.concat(val), []);

      if (typeFilterOptions.some((v) => v.isSelected)) {
        params = params.append(
          'status',
          typeFilterOptions
            .filter((v) => v.isSelected)
            .map((v) => v.value)
            .join(','),
        );
      }
    }

    return this.http
      .get(`${this.messageCenterApi}/messages/${messagesType}`, { params })
      .pipe(
        map(checkValidResponse),
        map((res) => this.formatMessageData(res)),
        catchError(logError),
      );
  }

  private formatMessageData = (
    response: MessagesResponse,
  ): MessagesResponse => {
    try {
      return {
        ...response,
        messages: response.messages.map((message: Message) => {
          const createdDateFormatted = this.datePipe.transform(message.created);
          const createdTimeFormat = this.datePipe.transform(
            message.created,
            'mediumTime',
          );
          return {
            ...message,
            desc: message.desc.replace(
              'UX-DATE',
              `${createdDateFormatted} at ${createdTimeFormat}`,
            ),
          };
        }),
      };
    } catch (error) {
      throw new TypeError(error);
    }
  };

  fetchMessageDetails(
    messagesType: MessageType,
    id: string,
  ): Observable<Message> {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );

    return this.http
      .get(`${this.messageCenterApi}/messages/details/${messagesType}/${id}`, {
        params,
      })
      .pipe(
        map(checkValidResponse),
        map((resp) => ({
          ...resp.details,
          desc: resp.details.desc?.replace(
            'UX-DATE',
            `${this.datePipe.transform(
              resp.details.created,
            )} at ${this.datePipe.transform(
              resp.details.created,
              'mediumTime',
            )}`,
          ),
        })),
        catchError(logError),
      );
  }

  updateMessages(update: MessageUpdate): Observable<void> {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );

    return this.http
      .put(`${this.messageCenterApi}/messages`, update, { params })
      .pipe(map(checkValidResponse), catchError(logError));
  }

  deleteMessages(ids: string[]): Observable<void> {
    const params: HttpParams = new HttpParams()
      .set('consumerCode', this.CONSUMER_CODE)
      .set('ids', ids.join(','));

    return this.http
      .delete(`${this.messageCenterApi}/messages`, { params })
      .pipe(map(checkValidResponse), catchError(logError));
  }

  getPresignedUrl(keys: string[]) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );

    const requestBody = keys.map((key) => ({
      key,
    }));
    return this.http
      .post(`${this.presignedUrlApi}`, requestBody, {
        params,
      })
      .pipe(
        map(checkValidResponse),
        map((res) => res.details),
        catchError(logError),
      );
  }
}
